/**
 * populate — first-time database initialization.
 *
 * Called once after the user's first login. Creates:
 *   1. #populated sentinel (prevents re-running)
 *   2. Tutorial project "Get to know To To-Do" with sample tasks
 *   3. #first-start setting (triggers FirstStartDialog)
 *
 * V1 reference: src/utils/populate/populate.ts
 * V2 simplification: No #inbox/#anyday objects (virtual in v2).
 */

import { db } from "@/db/schema"
import { populateSettings } from "./populateSettings"
import { populateGetToKnow } from "./populateGetToKnow"

/**
 * Check if database needs population and run it if so.
 *
 * Safe to call multiple times — checks for #populated sentinel.
 * Guards against running before auth (userId === "unauthorized")
 * to prevent writes with wrong ownership.
 *
 * IMPORTANT: Must be called AFTER initial sync completes (waitForInitialSync)
 * to prevent duplicate tutorial projects on existing accounts logging in
 * from a new device where #populated hasn't synced yet.
 */
export async function populateIfNeeded(): Promise<void> {
  try {
    // Guard: don't populate before authentication completes
    const userId = db.cloud.currentUserId
    if (!userId || userId === "unauthorized") {
      return
    }

    // Primary guard: check #populated sentinel (synced from server)
    const populated = await db.setting.get("#populated")
    if (populated) {
      return
    }

    // Secondary guard: if any task data exists, user isn't new
    // (handles edge case where #populated hasn't synced but tasks have)
    const existingCount = await db.totodo.count()
    if (existingCount > 0) {
      console.info(`[Populate] Skipped — ${existingCount} objects already exist.`)
      return
    }

    console.info("[Populate] First-time setup — creating tutorial data...")
    await populateSettings()
    await populateGetToKnow()
    console.info("[Populate] Done — tutorial project created.")
  } catch (error) {
    // Population is not critical — the app works without it.
    // Never crash or block the app over tutorial content.
    console.error("[Populate] Failed:", error)
  }
}
